import { useEffect, useMemo, useState } from "react";
import {
	X,
	Search,
	GraduationCap,
	Mail,
	Undo2,
	InfoIcon,
	School,
	CheckCheck,
	ChevronDown,
	ChevronUp,
	UserRoundPlus,
	UserRoundX,
	UserRoundCheck,
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useNotify } from "@/contexts/NotifyContext";
import { queryAlumniList, queryRegistrars, registrarToAlumni, updateRegistrar } from "@/services/DirectoryService";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { RegistrationStatus } from "iasma-datatypes";

/* ------------------------------------------------------------------ */
/*  Design tokens                                                     */
/*  Registrar's-office palette: deep ledger navy, aged-parchment      */
/*  ground, brass accent for the "seal" moment (match confidence).    */
/* ------------------------------------------------------------------ */
const theme = {
	ground: "#F4EFE4",
	ink: "#1E2A38",
	inkSoft: "#4A5768",
	line: "#DCD3BE",
	card: "#FFFEFB",
	brass: "#A9782F",
	brassSoft: "#E7D9B8",
	good: "#3E6B4F",
	goodSoft: "#E1EBE2",
	bad: "#9C4028",
	badSoft: "#F3E1DA",
	amber: "#B98A2E",
	amberSoft: "#F3E7CE",
};

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */
type AlumniRecord = {
	Id: number;
	Name: string;
	Graduation: number;
	UserId?: number;
	Classes?: string;
};

const FilterStatuses = ["PENDING", "APPROVED", "REJECTED", "ALL"];

type RegistrarRequest = {
	Name: string;
	Email: string;
	Graduation: number;
	Classes?: string;
	Status: RegistrationStatus; 
	Description?: string;

	// id: string;
	submittedAt: string;
	matchedAlumniId?: number;
	decisionNote?: string;
};

/* ------------------------------------------------------------------ */
/*  Matching: Sørensen–Dice bigram similarity for names,              */
/*  exact / near-miss comparison for graduation year.                 */
/* ------------------------------------------------------------------ */
function normalize(str: string): string {
	return str.trim().toLowerCase().replace(/\s+/g, " ");
}

function bigrams(str: string): string[] {
	const s = normalize(str).replace(/[^a-z0-9 ]/g, "");
	const grams: string[] = [];
	for (let i = 0; i < s.length - 1; i++) grams.push(s.slice(i, i + 2));
	return grams;
}

function diceSimilarity(a: string, b: string): number {
	const A = bigrams(a);
	const B = bigrams(b);
	if (A.length === 0 || B.length === 0) return normalize(a) === normalize(b) ? 1 : 0;
	const bag = new Map<string, number>();
	A.forEach((g) => bag.set(g, (bag.get(g) ?? 0) + 1));
	let overlap = 0;
	B.forEach((g) => {
		const count = bag.get(g) ?? 0;
		if (count > 0) {
			overlap++;
			bag.set(g, count - 1);
		}
	});
	return (2 * overlap) / (A.length + B.length);
}

type Candidate = {
	record: AlumniRecord;
	nameScore: number;
	yearDelta: number;
	overall: number;
};

function rankCandidates(alumniList: AlumniRecord[], req: RegistrarRequest): Candidate[] {
	return alumniList.map((record) => {
		const nameScore = diceSimilarity(req.Name, record.Name);
		const yearDelta = Math.abs(record.Graduation - req.Graduation);
		const yearScore = yearDelta === 0 ? 1 : yearDelta === 1 ? 0.6 : Math.max(0, 1 - yearDelta * 0.25);
		const overall = nameScore  * 0.8 + yearScore * 0.2;
		return { record, nameScore, yearDelta, overall };
	}).sort((x, y) => y.overall - x.overall);
}

function confidenceTier(overall: number): { label: string; color: string; soft: string } {
	if (overall >= 0.85) return { label: "Strong match", color: theme.good, soft: theme.goodSoft };
	if (overall >= 0.6) return { label: "Possible match", color: theme.amber, soft: theme.amberSoft };
	return { label: "No confident match", color: theme.bad, soft: theme.badSoft };
}

function StatusBadge({ status }: { status: RegistrationStatus }) {
	const map: Record<RegistrationStatus, { label: string; color: string; soft: string }> = {
		[RegistrationStatus.Pending]: { label: "Pending", color: theme.amber, soft: theme.amberSoft },
		[RegistrationStatus.Approved]: { label: "Approved", color: theme.good, soft: theme.goodSoft },
		[RegistrationStatus.Rejected]: { label: "Rejected", color: theme.bad, soft: theme.badSoft },
 	};
	const s = map[status];
	return (
		<span
			className="text-[11px] font-medium px-2 py-0.5 rounded-full uppercase tracking-wide"
			style={{ color: s.color, backgroundColor: s.soft }}
		>
			{s.label}
		</span>
	);
}

function IsStatus({ status, filter }: { status: RegistrationStatus; filter: string }) {
	switch (filter) {
		case "PENDING":
			return status === RegistrationStatus.Pending;
		case "APPROVED":
			return status === RegistrationStatus.Approved;
		case "REJECTED":
			return status === RegistrationStatus.Rejected;
		case "ALL":
			return true;
	}
}

/* ------------------------------------------------------------------ */
/*  Main component                                                    */
/* ------------------------------------------------------------------ */
export function RegisterApproval() {
	const { setToken } = useAuth()
	const { setNotify } = useNotify();
	
	const [registrars, setRegistrars] = useState<RegistrarRequest[]>([]);
	const [selectedRegistrar, setSelectedRegistrar] = useState<RegistrarRequest | null>(null);
	const [alumniList, setAlumniList] = useState<AlumniRecord[]>([]);

	const [query, setQuery] = useState("");
	const [filter, setFilter] = useState<string>("PENDING");


	const filtered = registrars.filter((r) => {
		const matchesFilter = IsStatus({ status: r.Status, filter });
		const matchesQuery = normalize(r.Name).includes(normalize(query)) || r.Email.toLowerCase().includes(query.toLowerCase());
		return matchesFilter && matchesQuery;
	});

	const pendingCount = registrars.filter((r) => r.Status === RegistrationStatus.Pending).length;

	// first loading: fetch registrars
	useEffect(() => {
		const fetchRegistrars = async () => {
			console.log("Fetching registrars...", registrars.length);
			if (registrars.length === 0) {
				try {
					setNotify(null);
					setToken(axios);
					const result: RegistrarRequest[] = await queryRegistrars();
					console.log("Fetched registrars:", result);
					setRegistrars(result);
				} catch (error: any) {
					setNotify({
						title: "Error Fetching Registrars",
						error,
						severity: 'error'
					});
				}
			}
		};
		fetchRegistrars();
	}, []);

	const onSelectRegistrar = (registrar: RegistrarRequest) => {
		if (registrar.Graduation !== selectedRegistrar?.Graduation) {
			setAlumniList([]); // reset alumni list when selecting a new registrar
		}
		setSelectedRegistrar(registrar);
	}

	const adjustSelectedYear = (delta: number) => {
		setSelectedRegistrar((prev) => {
			if (!prev) return prev;
			return { ...prev, Graduation: Math.max(0, prev.Graduation + delta) };
		});
	};

	// after select a registrar, or when the graduation year changes, fetch alumni list for that graduation year
	useEffect(() => {
		const fetchAlumniList = async () => {
			if (!selectedRegistrar) return;
			console.log("Fetching Alumni List...", alumniList.length, selectedRegistrar);
			try {
				setNotify(null);
				setToken(axios);
				setAlumniList([]);
				const result = await queryAlumniList(selectedRegistrar.Graduation);
				console.log("Fetched alumni list:", result);
				setAlumniList(result);
			} catch (error: any) {
				setNotify({
					title: "Error Fetching Alumni List",
					error,
					severity: 'error'
				});
			}
		};
		fetchAlumniList();
	}, [selectedRegistrar]);

	// after alumniList is fetched, re-rank candidates for the selected registrar
	const candidates = useMemo(() => {
		if (selectedRegistrar && alumniList) {
			return rankCandidates(alumniList, selectedRegistrar);
		}
		return [];
	}, [selectedRegistrar, alumniList]);

	function showCandicates() {
		let elements = [];
		if (selectedRegistrar && candidates.length > 0) {
			let legibleCandidates = candidates.filter(c => c.overall >= 0.3).slice(0, 10);
			for (let i = 0; i < legibleCandidates.length; i++) {
				const candidate = legibleCandidates[i];
				elements.push(<TableRow key={i}>
					<TableCell>{candidate.record.Name}</TableCell>
					<TableCell>{candidate.record.Graduation}</TableCell>
					<TableCell>{Math.round(candidate.overall * 100)}% · {confidenceTier(candidate.overall).label}</TableCell>
					<TableCell className="text-center">
						{candidate.record.UserId ? <CheckCheck size={16} /> : ''}
					</TableCell>
					<TableCell>
						<button className="bg-green-600 text-green-50 px-2.5 py-1.5 rounded-lg cursor-pointer inline-flex items-center gap-1.5 text-xs" 
							onClick={() => onRegistrarUpdate(RegistrationStatus.Approved, candidate.record.Id)}>
							<UserRoundPlus size={16} />
							<span>Link to this</span>
						</button>
					</TableCell>
				</TableRow>);
			}
			if (elements.length > 0) 
				return (
					<div className="overflow-x-auto">
						<Table className="w-full min-w-140 border-collapse text-sm">
							<TableHeader>
								<TableRow>
									<TableHead>Nama</TableHead>
									<TableHead>Angkatan</TableHead>
									<TableHead>Kesesuaian</TableHead>
									<TableHead className="text-center">Sign Up</TableHead>
									<TableHead></TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{elements}
							</TableBody>
						</Table>
					</div>
				)
		}
	}

	const onRegistrarUpdate = async (status: RegistrationStatus, alumniId?: number) => {
		if (selectedRegistrar) {
			try {
				setNotify(null);
				setToken(axios);
				let data = {
					Email: selectedRegistrar.Email,
					Status: status,
					AlumniId: status === RegistrationStatus.Approved ? alumniId : undefined
				}
				await updateRegistrar(data)
				// decide(selectedRegistrar.Email, alumniId, Status.Approved);
				setRegistrars((prev) =>
					prev.map((r) =>
						r.Email === selectedRegistrar.Email
							? { ...r, 
								Status: status, 
								matchedAlumniId: status === RegistrationStatus.Approved ? alumniId : undefined,
							}
							: r
					)
				);
				const remainingPending = registrars.filter((r) => r.Status === RegistrationStatus.Pending && r.Email !== selectedRegistrar.Email);
				if (remainingPending[0]) 
					setSelectedRegistrar(remainingPending[0])
				else
					setSelectedRegistrar(null)

			} catch (error: any) {
				setNotify({
					title: "Error Update Registrar",
					error,
					severity: 'error'
				});
			}
		}
	}

	const onRegistrarNewAlumni = async () => {
		if (selectedRegistrar) {
			setNotify(null);
			try {
				setToken(axios);
				let result: any = await registrarToAlumni(selectedRegistrar);
				console.log('registrarToAlumni result:', result);
				if (result.Id) {
					await onRegistrarUpdate(RegistrationStatus.Approved, result.Id);
				} else {
					setNotify({ title: "Registration New Alumni Failed", error: { message: result.message }, severity: 'error' });
				}
			} catch (error: any) {
				setNotify({ title: "Registration New Alumni Failed", error, severity: 'error' });
			}
		}
	}

	const onSetFilter = (filter: string) => {
		setSelectedRegistrar(null)
		setFilter(filter);
	}

	return (
		<div className="min-h-screen w-full overflow-x-hidden">
			<style>{`
        @import url('https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,400;8..60,600;8..60,700&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@500&display=swap');
      `}</style>

			<div className="mx-auto max-w-6xl px-0 py-3 sm:px-3 lg:px-4 lg:py-5" style={{ fontFamily: "'Inter', sans-serif" }}>
				{/* Header */}
				<div className="mb-3 flex flex-col gap-1 border-b sm:mb-2 sm:flex-row sm:items-end sm:justify-between" style={{ borderColor: theme.line }}>
					<div>
						<div className="flex flex-row justify-between align-middle items-center ">
						<div
							className="mb-2 text-[11px] uppercase tracking-[0.2em]"
							style={{ color: theme.brass, fontFamily: "'IBM Plex Mono', monospace" }}
						>
							Registrasi Gmail
						</div>
						<div className="text-left sm:text-right flex flex-row align-middle items-center gap-1">
							<div className="text-md font-semibold" style={{ color: theme.ink, fontFamily: "'Source Serif 4', serif" }}>
								{pendingCount}
							</div>
							<div className="text-xs" style={{ color: theme.inkSoft }}>
								pending
							</div>
						</div>
						</div>
						<h1
							className="text-md sm:text-lg"
							style={{ color: theme.ink, fontFamily: "'Source Serif 4', serif", fontWeight: 600 }}
						>
							Pemetaan <b>GMail</b> dengan <b>Alumni</b>
						</h1>
					</div>
				</div>

				<div className="grid grid-cols-1 gap-2 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] lg:gap-4">
					{/* Queue */}
					<div className="rounded-lg overflow-hidden" style={{ backgroundColor: theme.card, border: `1px solid ${theme.line}` }}>
						<div className="border-b p-2" style={{ borderColor: theme.line }}>
							<div className="relative mb-2">
								<Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: theme.inkSoft }} />
								<input
									value={query}
									onChange={(e) => setQuery(e.target.value)}
									placeholder="Search name or email"
									className="w-full rounded border px-8 py-1.5 text-sm outline-none"
									style={{ backgroundColor: theme.ground, color: theme.ink, border: `1px solid ${theme.line}` }}
								/>
							</div>
							<div className="flex flex-wrap gap-1.5">
								{FilterStatuses.map((f) => (
									<button
										key={f}
										onClick={() => onSetFilter(f)}
										className="text-[11px] px-2 py-1 rounded-full capitalize transition-colors"
										style={{
											backgroundColor: filter === f ? theme.ink : "transparent",
											color: filter === f ? theme.card : theme.inkSoft,
											border: `1px solid ${filter === f ? theme.ink : theme.line}`,
										}}
									>
										{f}
									</button>
								))}
							</div>
						</div>

						<div className="max-h-140 overflow-y-auto">
							{filtered.length === 0 && (
								<div className="p-6 text-sm text-center" style={{ color: theme.inkSoft }}>
									No requests match.
								</div>
							)}
							{filtered.map((r) => {
								const isSelected = r === selectedRegistrar;
								return (
									<button
										key={r.Email}
										onClick={() => onSelectRegistrar(r)}
										className="w-full text-left px-2 py-3 border-b transition-colors"
										style={{
											borderColor: theme.line,
											backgroundColor: isSelected ? theme.brassSoft : "transparent",
										}}
									>
										<div className="flex items-center justify-between mb-1">
											<span className="text-sm font-medium truncate" style={{ color: theme.ink }}>
												{r.Name}
											</span>
											<StatusBadge status={r.Status} />
										</div>
										<div className="flex items-center gap-1 text-xs" style={{ color: theme.inkSoft }}>
											<GraduationCap size={12} />
											<span>Angkatan {r.Graduation}</span>
										</div>
									</button>
								);
							})}
						</div>
					</div>

					{/* Detail */}
					<div className="rounded-lg p-0.5 sm:p-4" style={{ backgroundColor: theme.card, border: `1px solid ${theme.line}` }}>
						{!selectedRegistrar ? (
							<div className="flex h-full items-center justify-center text-sm" style={{ color: theme.inkSoft }}>
								Pilih satu registrar dari antrian.
							</div>
						) : (
							<>
								<div className="mb-6 p-1 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
									<div className="min-w-0">
										<h2 className="mb-1 text-xl" style={{ color: theme.ink, fontFamily: "'Source Serif 4', serif", fontWeight: 600 }}>
											{selectedRegistrar.Name}
										</h2>
										<div className="flex items-center gap-1.5 text-sm break-all" style={{ color: theme.inkSoft }}>
											<Mail size={13} />
											{selectedRegistrar.Email}
										</div>
										<div className="inline-flex items-center gap-2 text-sm" style={{ color: theme.inkSoft }}>
										<div className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white/80 px-1.5 py-0.5">
											<button
												type="button"
												onClick={() => adjustSelectedYear(-1)}
												className="flex h-6 w-6 items-center justify-center rounded border border-slate-200 bg-slate-50 text-slate-600 transition hover:bg-slate-100"
												aria-label="Kurangi angkatan"
											>
												<ChevronDown size={12} />
											</button>
											<div className="inline-flex items-center gap-1.5 px-1">
												<GraduationCap size={13} />
												<span>{selectedRegistrar.Graduation}</span>
											</div>
											<button
												type="button"
												onClick={() => adjustSelectedYear(1)}
												className="flex h-6 w-6 items-center justify-center rounded border border-slate-200 bg-slate-50 text-slate-600 transition hover:bg-slate-100"
												aria-label="Tambah angkatan"
											>
												<ChevronUp size={12} />
											</button>
										</div>
									</div>
									{ selectedRegistrar.Classes && (
											<div className="flex items-center gap-1.5 text-sm" style={{ color: theme.inkSoft }}>
												<School size={13} />
												{selectedRegistrar.Classes}
											</div>
										)}
										{ selectedRegistrar.Description && (
											<div className="flex items-center gap-1.5 text-sm" style={{ color: theme.inkSoft }}>
												<InfoIcon size={13} />
												{selectedRegistrar.Description}
											</div>
										)}
									</div>
									<StatusBadge status={selectedRegistrar.Status} />
								</div>

								{ (selectedRegistrar.Status === RegistrationStatus.Pending) && showCandicates() } 

								{selectedRegistrar.Status === RegistrationStatus.Pending ? (
									<div className="mt-2 flex flex-col gap-3 rounded-md px-4 py-3 sm:flex-row sm:items-center sm:justify-between" >
										<span className="text-sm text-gray-500">Tidak ada record yang sesuai</span>
										<div className="flex flex-row gap-2">
										<button className="bg-red-200 px-2.5 py-1.5 rounded-lg cursor-pointer inline-flex items-center gap-1.5 text-xs" 
											onClick={() => onRegistrarUpdate(RegistrationStatus.Rejected)}>
											<UserRoundX size={16} />
											<span>Reject</span>
										</button>
										<button className="bg-amber-300 px-2.5 py-1.5 rounded-lg cursor-pointer inline-flex items-center gap-1.5 text-xs" 
											onClick={() => onRegistrarNewAlumni()}>
											<UserRoundPlus size={16} />
											<span>Buat Baru</span>
										</button>
										</div>
									</div>
								) : (
									<div
										className="flex flex-col gap-3 rounded-md px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
										style={{ backgroundColor: theme.ground, border: `1px solid ${theme.line}` }}
									>
										<span className="text-sm" style={{ color: theme.inkSoft }}>
											{selectedRegistrar.Status === RegistrationStatus.Approved
												? "Approved and linked to the matched alumni record."
												: selectedRegistrar.decisionNote ?? "Rejected."}
										</span>
										<button className="bg-amber-600 text-amber-50 px-2.5 py-1.5 rounded-lg cursor-pointer inline-flex items-center gap-1.5 text-xs" 
											onClick={() => onRegistrarUpdate(RegistrationStatus.Pending)}>
											<Undo2 size={16} />
											<span>Undo</span>
										</button>
									</div>
								)}
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}